<!--
 * @Author: AI Assistant
 * @Date: 2026-01-09
 * @Description: Banner 音乐详情弹窗组件
 * @FilePath: /weryai-web/components/Tool/BannerMusicModal.vue
-->
<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { useMusicPlayerStore } from '~/stores/musicPlayer'
import { useUserStore } from '@/stores/user'
import { noticeMessagePopup } from '~/utils/common'
import { showLoginModal } from '~/utils/emitter'
import { changeUserLikeStateApi } from '~/api/inspiration'

const { t, locale } = useI18n()
const musicPlayer = useMusicPlayerStore()
const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)

// Props
const props = defineProps<{
    visible: boolean
    musicData: any
}>()

// Emits
const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void
}>()

// 获取灵感 ID
const getInspirationId = (item: any) => item?.ins_id || item?.id

// 当前音乐 ID
const currentMusicId = computed(() => getInspirationId(props.musicData))

// 参考音频的 ID（用于区分）
const refAudioId = computed(() => `ref_${currentMusicId.value}`)

// 进度条元素引用
const progressBarRef = ref<HTMLDivElement>()

// 从 Store 获取播放状态
const isPlaying = computed(() =>
    musicPlayer.isCurrentPlaying(currentMusicId.value)
)
const isLoading = computed(() =>
    musicPlayer.isCurrentLoading(currentMusicId.value)
)
const currentTime = computed(() => {
    if (musicPlayer.currentId === currentMusicId.value) {
        return musicPlayer.currentTime
    }
    return 0
})
const duration = computed(() => {
    if (musicPlayer.currentId === currentMusicId.value) {
        return musicPlayer.duration
    }
    return 0
})

// 进度百分比
const progressPercent = computed(() => {
    if (musicPlayer.currentId === currentMusicId.value) {
        return musicPlayer.progressPercent
    }
    return 0
})

// 格式化时间
const formatTime = (seconds: number): string => {
    return musicPlayer.formatTime(seconds)
}

// 音乐封面图
const musicCoverImage = computed(() => props.musicData?.output || '')

// 音频 URL
const musicAudioUrl = computed(() => props.musicData?.output_audio || '')

// 音乐标题
const musicTitle = computed(
    () =>
        props.musicData?.music?.music_title ||
        props.musicData?.param?.prompt ||
        t('inspiration.untitledMusic')
)

// 歌词
const musicLyrics = computed(
    () => props.musicData?.music?.generated_lyrics || props.musicData?.param?.lyrics || ''
)

// 参考音频 URL（从 param.audio_urls 数组取第一条）
const referenceAudioUrl = computed(
    () => props.musicData?.param?.audio_urls?.[0] || ''
)

// 参考音频名称（从 param.name 字段获取）
const referenceAudioName = computed(() => {
    return props.musicData?.param?.name || t('inspiration.referenceAudio')
})

// 参考音频播放状态（从 Store 获取）
const isRefAudioPlaying = computed(() =>
    musicPlayer.isCurrentPlaying(refAudioId.value)
)
const isRefAudioLoading = computed(() =>
    musicPlayer.isCurrentLoading(refAudioId.value)
)

// 本地点赞状态（用于乐观更新）
const localHasLike = ref(false)
const localLikesNum = ref(0)

// 监听 musicData 变化，同步点赞状态
watch(
    () => props.musicData,
    (newVal) => {
        if (newVal) {
            localHasLike.value = !!newVal.has_like
            localLikesNum.value = newVal.likes_num || 0
        }
    },
    { immediate: true, deep: true }
)

// 播放/暂停参考音频
const toggleRefAudioPlay = () => {
    if (!referenceAudioUrl.value) {
        noticeMessagePopup(t('inspiration.noAudio'), '', 'warning')
        return
    }
    musicPlayer.play(refAudioId.value, referenceAudioUrl.value)
}

// 下载参考音频
const downloadRefAudio = async () => {
    if (!referenceAudioUrl.value) {
        noticeMessagePopup(t('inspiration.noAudio'), '', 'warning')
        return
    }
    const filename = `weryai_ref_audio_${Date.now()}.mp3`
    downloadMedia(referenceAudioUrl.value, filename)
}

// 播放/暂停
const toggleMusicPlay = () => {
    if (!musicAudioUrl.value) {
        noticeMessagePopup(t('inspiration.noAudio'), '', 'warning')
        return
    }
    musicPlayer.play(currentMusicId.value, musicAudioUrl.value)
}

// 进度条点击
const handleAudioSeek = (e: MouseEvent) => {
    if (!progressBarRef.value) return
    if (musicPlayer.currentId !== currentMusicId.value) return

    const rect = progressBarRef.value.getBoundingClientRect()
    const percent = ((e.clientX - rect.left) / rect.width) * 100
    musicPlayer.seekByPercent(percent)
}

// 回退5秒
const seekBackward = () => {
    if (musicPlayer.currentId !== currentMusicId.value) return
    musicPlayer.seek(Math.max(0, musicPlayer.currentTime - 5))
}

// 前进5秒
const seekForward = () => {
    if (musicPlayer.currentId !== currentMusicId.value) return
    musicPlayer.seek(Math.min(musicPlayer.duration, musicPlayer.currentTime + 5))
}

// 做同款
const handleMakeSimilar = () => {
    const item = props.musicData
    if (!item) return
    if (!import.meta.client) return

    const origin = window.location.origin
    const timestamp = Date.now()
    const path = `${origin}/${locale.value}/text-to-music?customType=inspiration&t=${timestamp}`

    localStorage.setItem('inspirationData', JSON.stringify(item))
    handleClose()
    location.href = path
}

// 复制歌词
const copyLyrics = async () => {
    if (!musicLyrics.value) {
        noticeMessagePopup(t('inspiration.noLyrics'), '', 'warning')
        return
    }
    try {
        await navigator.clipboard.writeText(musicLyrics.value)
        noticeMessagePopup(t('inspiration.copySuccess'), '', 'success', 3000)
    } catch (err) {
        console.error('复制失败:', err)
        noticeMessagePopup(t('inspiration.copyFailed'), '', 'error', 3000)
    }
}

// 复制提示词
const copyPrompt = async () => {
    const promptText = props.musicData?.param?.prompt
    if (promptText) {
        try {
            await navigator.clipboard.writeText(promptText)
            noticeMessagePopup(t('inspiration.copySuccess'), '', 'success', 3000)
        } catch (err) {
            console.error('复制失败:', err)
            noticeMessagePopup(t('inspiration.copyFailed'), '', 'error', 3000)
        }
    }
}

// 复制链接
const copyLink = async () => {
    if (!import.meta.client) return
    const origin = window.location.origin
    const inspirationId = getInspirationId(props.musicData)
    const path = `${origin}/creation/${inspirationId}`
    try {
        await navigator.clipboard.writeText(path)
        noticeMessagePopup(t('inspiration.copySuccess'), '', 'success', 3000)
    } catch (err) {
        console.error('复制失败:', err)
        noticeMessagePopup(t('inspiration.copyFailed'), '', 'error', 3000)
    }
}

// 下载音乐
const downloadMusic = async () => {
    if (!musicAudioUrl.value) {
        noticeMessagePopup(t('inspiration.noAudio'), '', 'warning')
        return
    }
    const inspirationId = getInspirationId(props.musicData)
    const filename = `weryai_music_${inspirationId}.mp3`
    downloadMedia(musicAudioUrl.value, filename)
}

// 文件下载
const downloadMedia = async (url: string, filename: string) => {
    noticeMessagePopup(t('inspiration.preparingDownload'), '', 'success', 3000)
    try {
        const response = await fetch(url, { mode: 'cors', cache: 'no-cache' })
        if (!response.ok) {
            throw new Error(`下载失败: ${response.status} ${response.statusText}`)
        }
        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.style.display = 'none'
        link.href = blobUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
        console.error('下载失败:', error)
        noticeMessagePopup(t('inspiration.downloadFailed'), '', 'error', 3000)
    }
}

// 点赞
const handleLike = async () => {
    if (!isLoggedIn.value) {
        showLoginModal()
        return
    }

    const inspirationId = getInspirationId(props.musicData)
    if (!inspirationId) return

    // 乐观更新
    const prevHasLike = localHasLike.value
    const prevLikesNum = localLikesNum.value
    localHasLike.value = !prevHasLike
    localLikesNum.value = localHasLike.value ? prevLikesNum + 1 : Math.max(0, prevLikesNum - 1)

    try {
        const res = await changeUserLikeStateApi({
            ins_id: inspirationId,
            like: localHasLike.value,
        })
        if (res.status !== 200) {
            // 回滚
            localHasLike.value = prevHasLike
            localLikesNum.value = prevLikesNum
            noticeMessagePopup(res.message || t('inspiration.operationFailed'), '', 'warning')
        }
    } catch (error) {
        // 回滚
        localHasLike.value = prevHasLike
        localLikesNum.value = prevLikesNum
        console.error('点赞失败:', error)
        noticeMessagePopup(t('inspiration.likeFailed'), '', 'error')
    }
}

// 关闭弹窗
const handleClose = () => {
    // 如果正在播放当前弹窗的音乐，则停止
    if (
        musicPlayer.currentId === currentMusicId.value ||
        musicPlayer.currentId === refAudioId.value
    ) {
        musicPlayer.stop()
    }
    emit('update:visible', false)
}

// 阻止滚动穿透
const preventScroll = (e: Event) => {
    const target = (e.target as HTMLElement | null) || null
    if (!target) return

    const scrollElement = target.closest('.music-content-scroll') || target.closest('.lyrics-display-area')

    if (scrollElement) {
        if (!(e instanceof WheelEvent)) return
        const scrollTop = scrollElement.scrollTop
        const scrollHeight = scrollElement.scrollHeight
        const height = scrollElement.clientHeight
        const delta = e.deltaY
        const isAtTop = scrollTop === 0 && delta < 0
        const isAtBottom = scrollTop + height >= scrollHeight && delta > 0
        if (isAtTop || isAtBottom) {
            e.preventDefault()
        }
        return
    }
    e.preventDefault()
}

// 监听弹窗状态，控制 body 滚动
watch(
    () => props.visible,
    (newVal) => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return
        if (newVal) {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
            document.body.style.overflow = 'hidden'
            document.body.style.paddingRight = scrollbarWidth + 'px'
        } else {
            document.body.style.overflow = ''
            document.body.style.paddingRight = ''
        }
    },
    { immediate: true }
)

onBeforeUnmount(() => {
    if (typeof document !== 'undefined') {
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
    }
})
</script>

<template>
    <!-- 遮罩层 -->
    <div
        v-if="visible"
        class="outside-mask-container flex items-center justify-center"
        @wheel="preventScroll"
        @touchmove="preventScroll"
        @click.self="handleClose"
    >
        <div class="mask music-mask">
            <!-- 关闭按钮 -->
            <button
                class="close-btn"
                :aria-label="t('common.close')"
                @click="handleClose"
            >
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <path
                        d="M0.308838 0.307739C0.719384 -0.102559 1.38467 -0.102635 1.79517 0.307739L6.99829 5.51184L12.2024 0.308716C12.6129 -0.101641 13.2782 -0.101499 13.6887 0.308716C14.0993 0.719337 14.0993 1.3854 13.6887 1.79602L8.4856 6.99915L13.6897 12.2042C14.1002 12.6148 14.1002 13.2799 13.6897 13.6906C13.2791 14.1012 12.6139 14.1012 12.2034 13.6906L6.99829 8.48547L1.79419 13.6915C1.38361 14.1021 0.718438 14.1021 0.307861 13.6915C-0.102587 13.2809 -0.102617 12.6158 0.307861 12.2052L5.51196 6.99915L0.308838 1.79504C-0.101739 1.38442 -0.101739 0.718361 0.308838 0.307739Z"
                        fill="white"
                    />
                </svg>
            </button>

            <!-- 音乐详情布局 -->
            <div class="music-detail-layout">
                <!-- 左侧播放区域 -->
                <div class="music-left-panel">
                    <!-- 背景层：封面图 + 高斯模糊 -->
                    <div
                        class="music-bg-blur"
                        :style="{ backgroundImage: `url(${musicCoverImage})` }"
                    ></div>

                    <!-- 有歌词时：显示歌曲名 + 复制按钮 + 歌词区域 -->
                    <div v-if="musicLyrics" class="lyrics-panel">
                        <!-- 歌曲名称 - 修复标题顺序：从 h3 改为 h2（因为右侧有 h2.song-title-single，这里应该也是 h2） -->
                        <h2 class="lyrics-song-title">{{ musicTitle }}</h2>
                        <!-- 复制歌词按钮 -->
                        <button class="copy-lyrics-btn" @click="copyLyrics">
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                            <span>{{ t('inspiration.copyLyrics') }}</span>
                        </button>
                        <!-- 歌词滚动区域 -->
                        <div class="lyrics-display-area">
                            <p class="lyrics-display-text">{{ musicLyrics }}</p>
                        </div>
                    </div>

                    <!-- 无歌词时：黑胶唱片容器 -->
                    <div v-else class="vinyl-wrapper">
                        <!-- 黑胶唱片 -->
                        <div
                            class="vinyl-record"
                            :class="{ spinning: isPlaying }"
                        >
                            <div class="vinyl-outer"></div>
                            <div class="vinyl-inner">
                                <img
                                    v-if="musicCoverImage"
                                    :src="musicCoverImage"
                                    alt="Music cover"
                                    class="vinyl-cover"
                                />
                                <div v-else class="vinyl-cover-placeholder">
                                    <svg
                                        class="w-12 h-12 text-white/30"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                    >
                                        <path
                                            d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <!-- 唱针 -->
                        <img
                            src="~/assets/images/common/music-open.png"
                            alt="needle"
                            class="music-needle"
                            :class="{ playing: isPlaying }"
                        />
                    </div>

                    <!-- 底部控制区域 -->
                    <div class="music-control-panel">
                        <!-- 进度条 -->
                        <div class="progress-row">
                            <span class="time-text">{{ formatTime(currentTime) }}</span>
                            <div
                                class="progress-bar"
                                ref="progressBarRef"
                                @click="handleAudioSeek"
                            >
                                <div
                                    class="progress-fill"
                                    :style="{ width: progressPercent + '%' }"
                                ></div>
                                <div
                                    class="progress-thumb"
                                    :style="{ left: progressPercent + '%' }"
                                ></div>
                            </div>
                            <span class="time-text">{{ formatTime(duration) }}</span>
                        </div>

                        <!-- 播放按钮组 -->
                        <div class="control-buttons">
                            <!-- 回退5秒 -->
                            <button class="seek-btn" @click="seekBackward">
                                <img
                                    src="~/assets/images/common/seek-backward.png"
                                    alt="backward"
                                    class="seek-icon"
                                />
                            </button>

                            <!-- 播放/暂停 -->
                            <button
                                class="play-btn-large"
                                type="button"
                                :aria-label="
                                    isLoading
                                        ? t('common.loading')
                                        : isPlaying
                                          ? `${t('common.pause')} ${musicTitle || t('inspiration.untitledMusic')}`
                                          : `${t('common.play')} ${musicTitle || t('inspiration.untitledMusic')}`
                                "
                                @click="toggleMusicPlay"
                            >
                                <!-- 加载中 -->
                                <svg
                                    v-if="isLoading"
                                    class="animate-spin"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    aria-hidden="true"
                                >
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-opacity="0.3"
                                    />
                                    <path
                                        d="M12 2a10 10 0 0 1 10 10"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                    />
                                </svg>
                                <!-- 播放 -->
                                <svg
                                    v-else-if="!isPlaying"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                                <!-- 暂停 -->
                                <svg
                                    v-else
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                </svg>
                            </button>

                            <!-- 前进5秒 -->
                            <button class="seek-btn" @click="seekForward">
                                <img
                                    src="~/assets/images/common/seek-forward.png"
                                    alt="forward"
                                    class="seek-icon"
                                />
                            </button>
                        </div>
                    </div>
                </div>

                <!-- 右侧信息区域 -->
                <div class="music-right-panel">
                    <!-- 标题区 -->
                    <div class="music-header">
                        <div class="song-label">SONG TITLE</div>
                        <h2 class="song-title-single">{{ musicTitle }}</h2>
                        <div class="user-info" v-if="musicData?.user">
                            <img
                                v-if="musicData.user.avatar"
                                :src="musicData.user.avatar"
                                alt="User avatar"
                                class="user-avatar"
                            />
                            <img
                                v-else
                                src="https://static.weryai.com/growth/image/9ead8a3f9b5dd9d44367d3aba6ac8a93.webp"
                                alt="Default avatar"
                                class="user-avatar"
                            />
                            <span class="user-name">{{ musicData.user.user_name }}</span>
                        </div>
                    </div>

                    <!-- 内容滚动区域 -->
                    <div class="music-content-scroll">
                        <!-- 提示词区 -->
                        <div class="info-section-fixed">
                            <div class="section-header">
                                <span class="section-label">PROMPT</span>
                                <button
                                    class="copy-icon-btn"
                                    type="button"
                                    :aria-label="t('inspiration.copyPrompt')"
                                    @click="copyPrompt"
                                    v-if="musicData?.param?.prompt"
                                >
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        aria-hidden="true"
                                    >
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                    </svg>
                                </button>
                            </div>
                            <div class="section-content-scroll">
                                {{ musicData?.param?.prompt || t('inspiration.noPrompt') }}
                            </div>
                        </div>

                        <!-- 参考音频区 -->
                        <div class="info-section-fixed" v-if="referenceAudioUrl">
                            <div class="section-header">
                                <span class="section-label">{{ t('inspiration.referenceAudio') }}</span>
                            </div>
                            <div class="ref-audio-card">
                                <!-- 播放按钮 -->
                                <button class="ref-audio-play-btn" @click="toggleRefAudioPlay">
                                    <!-- 加载中 -->
                                    <svg
                                        v-if="isRefAudioLoading"
                                        class="animate-spin"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <circle
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-opacity="0.3"
                                        />
                                        <path
                                            d="M12 2a10 10 0 0 1 10 10"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                        />
                                    </svg>
                                    <!-- 播放 -->
                                    <svg
                                        v-else-if="!isRefAudioPlaying"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                    >
                                        <path d="M8 5v14l11-7z" />
                                    </svg>
                                    <!-- 暂停 -->
                                    <svg
                                        v-else
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                    >
                                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                    </svg>
                                </button>

                                <!-- 音频名称 -->
                                <span class="ref-audio-name">{{ referenceAudioName }}</span>

                                <!-- 下载按钮 -->
                                <button class="ref-audio-download-btn" @click="downloadRefAudio">
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                    >
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="7,10 12,15 17,10" />
                                        <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- 底部操作栏 -->
                    <div class="music-footer">
                        <!-- 下载 -->
                        <button
                            class="footer-btn"
                            type="button"
                            :aria-label="`${t('common.download')} ${musicTitle || t('inspiration.untitledMusic')}`"
                            @click="downloadMusic"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                aria-hidden="true"
                            >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7,10 12,15 17,10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                        </button>

                        <!-- 分享链接 -->
                        <button
                            class="footer-btn"
                            type="button"
                            :aria-label="t('inspiration.copyLink')"
                            @click="copyLink"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                aria-hidden="true"
                            >
                                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                            </svg>
                        </button>

                        <!-- 点赞 -->
                        <button
                            class="footer-btn like-btn"
                            :class="{ liked: localHasLike }"
                            @click="handleLike"
                        >
                            <svg
                                v-if="!localHasLike"
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.5"
                            >
                                <path
                                    d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                />
                            </svg>
                            <svg
                                v-else
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="#FF5353"
                            >
                                <path
                                    d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                />
                            </svg>
                            <span>{{ localLikesNum }}</span>
                        </button>

                        <!-- 做同款 -->
                        <button class="make-similar-btn" @click="handleMakeSimilar">
                            <img
                                src="~/assets/images/common/make-similar-icon.png"
                                alt="make similar"
                                class="make-similar-icon"
                            />
                            <span>{{ t('menus.aiMusicPage.makeSimilar') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="less" scoped>
.outside-mask-container {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.7);
    z-index: 99;
    overflow: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
}

.mask {
    position: absolute;
    top: 15%;
    bottom: 15%;
    left: 15%;
    right: 15%;
    width: auto;
    height: auto;
    padding: 20px;
    overflow: auto;
    border-radius: 24px;
    background-color: #22242d;
    // 确保默认文本颜色有足够对比度
    color: #ffffff; // 白色文本在深色背景上对比度足够

    &.music-mask {
        position: relative;
        top: auto;
        bottom: auto;
        left: auto;
        right: auto;
        width: 100%;
        max-width: 988px;
        height: 100%;
        max-height: 774px;
        overflow: hidden;
    }

    .close-btn {
        position: absolute;
        right: 20px;
        top: 20px;
        z-index: 10;
        border-radius: 5px;
        border: none;
        background: transparent;
        cursor: pointer;
        color: #cfd3dc;

        &:hover {
            background: #22242d;
            color: #fff;
        }
    }
}

// ==================== 音乐类型样式 ====================
.music-detail-layout {
    width: 100%;
    height: 100%;
    min-height: 0;
    display: flex;
    background: transparent;
}

// 左侧播放区域
.music-left-panel {
    flex: 0 0 45%;
    height: 100%;
    min-height: 0;
    border-radius: 20px;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

// 背景模糊
.music-bg-blur {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    z-index: 0;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.6);
    }
}

// 歌词面板容器
.lyrics-panel {
    position: absolute;
    top: 48px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
    width: 90%;
    max-width: 380px;
    bottom: 160px;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
}

// 歌曲名称
.lyrics-song-title {
    font-size: 32px;
    font-weight: bold;
    color: #fff;
    margin: 0 0 12px 0;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
    flex-shrink: 0;
}

// 复制歌词按钮
.copy-lyrics-btn {
    height: 27px;
    padding: 0 12px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    transition: all 0.2s;
    margin-bottom: 16px;
    flex-shrink: 0;

    &:hover {
        background: rgba(255, 255, 255, 0.15);
        color: #fff;
    }
}

// 歌词滚动区域
.lyrics-display-area {
    flex: 1;
    width: 100%;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.lyrics-display-text {
    font-size: 13px;
    line-height: 2.2;
    color: rgba(255, 255, 255, 0.8);
    white-space: pre-wrap;
    word-break: break-word;
    text-align: center;
    padding: 10px 0;
}

// 黑胶容器
.vinyl-wrapper {
    position: relative;
    z-index: 1;
    width: 60%;
    max-width: 320px;
    aspect-ratio: 1 / 1;
    margin-bottom: 60px;
}

// 黑胶唱片
.vinyl-record {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;

    &.spinning {
        .vinyl-outer {
            animation: spin 3s linear infinite;
        }
        .vinyl-inner {
            animation: spin-center 3s linear infinite;
        }
    }
}

.vinyl-outer {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: rgba(17, 17, 17, 1);
    box-shadow:
        0 0 0 2px rgba(0, 0, 255, 0.1),
        0 10px 40px rgba(0, 0, 0, 0.5);
}

.vinyl-inner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 50%;
    height: 50%;
    border-radius: 50%;
    overflow: hidden;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}

.vinyl-cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.vinyl-cover-placeholder {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #3a3a3a 0%, #1a1a1a 100%);
    display: flex;
    align-items: center;
    justify-content: center;
}

// 唱针
.music-needle {
    position: absolute;
    width: 28%;
    max-width: 90px;
    aspect-ratio: 1 / 2;
    right: -10%;
    top: -12%;
    transform: rotate(0deg);
    transform-origin: 75% 15%;
    transition: transform 0.3s ease;
    z-index: 2;

    &.playing {
        transform: rotate(25deg);
    }
}

// 底部控制区域
.music-control-panel {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
    width: 85%;
    max-width: 380px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    border-radius: 24px;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

// 进度条行
.progress-row {
    display: flex;
    align-items: center;
    gap: 12px;

    .time-text {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.6);
        min-width: 36px;
        text-align: center;
    }

    .progress-bar {
        flex: 1;
        height: 4px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 2px;
        cursor: pointer;
        position: relative;

        &:hover {
            height: 6px;

            .progress-thumb {
                opacity: 1;
            }
        }

        .progress-fill {
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
            background: linear-gradient(90deg, #99f768, #7ed957);
            border-radius: 2px;
            transition: width 0.1s linear;
        }

        .progress-thumb {
            position: absolute;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 12px;
            height: 12px;
            background: #99f768;
            border-radius: 50%;
            opacity: 0;
            transition: opacity 0.2s;
        }
    }
}

// 控制按钮组
.control-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 30px;
}

.seek-btn {
    width: 24px;
    height: 24px;
    background: transparent;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    transition: all 0.2s;

    .seek-icon {
        width: 24px;
        height: 24px;
        object-fit: contain;
    }

    &:hover {
        opacity: 0.8;
    }
}

.play-btn-large {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #fff;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #000;
    transition: all 0.2s;

    &:hover {
        transform: scale(1.05);
    }
}

// 右侧信息区域
.music-right-panel {
    flex: 0 0 55%;
    height: 100%;
    min-height: 0;
    padding: 0 24px 20px 34px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

// 标题区
.music-header {
    flex-shrink: 0;
    margin-bottom: 10px;

    .song-label {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.7); // 提高对比度：从 0.4 提升到 0.7
        letter-spacing: 1px;
        margin-bottom: 6px;
    }

    .song-title-single {
        font-size: 24px;
        font-weight: 600;
        color: #fff;
        margin: 0 0 10px 0;
        line-height: 1.2;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .user-info {
        display: flex;
        align-items: center;
        gap: 8px;

        .user-avatar {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            object-fit: cover;
        }

        .user-name {
            font-size: 12px;
            color: rgba(255, 255, 255, 0.6);
        }
    }
}

// 内容滚动区域
.music-content-scroll {
    flex: 1 1 0;
    overflow-y: scroll;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-right: 8px;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 2px;

        &:hover {
            background: rgba(255, 255, 255, 0.3);
        }
    }
}

// 提示词/歌词区块
.info-section-fixed {
    .section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 6px;

        .section-label {
            font-size: 11px;
            color: rgba(255, 255, 255, 0.7); // 提高对比度：从 0.4 提升到 0.7
            letter-spacing: 1px;
        }
    }

    .section-content-scroll {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        padding: 10px;
        font-size: 13px;
        color: rgba(255, 255, 255, 0.8);
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-word;
    }
}

.copy-icon-btn {
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
    }
}

// 参考音频卡片样式
.ref-audio-card {
    display: flex;
    align-items: center;
    gap: 12px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 10px 14px;
}

.ref-audio-play-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #fff;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #000;
    flex-shrink: 0;
    transition: all 0.2s;

    &:hover {
        transform: scale(1.05);
    }
}

.ref-audio-name {
    flex: 1;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.8);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ref-audio-download-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: transparent;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.5);
    flex-shrink: 0;
    transition: all 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
    }
}

// 底部操作栏
.music-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-top: 12px;
    margin-top: 10px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    height: 56px;
    flex-shrink: 0;
}

.footer-btn {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.2s;
    border: 1px solid rgba(255, 255, 255, 0.1);

    &:hover {
        background: rgba(255, 255, 255, 0.15);
        color: #fff;
    }

    &.like-btn {
        gap: 6px;
        width: auto;
        padding: 0 16px;

        span {
            font-size: 13px;
        }

        &.liked {
            color: #ff5353;
        }
    }
}

.make-similar-btn {
    flex: 1;
    height: 44px;
    border-radius: 10px;
    background: #99f768;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    cursor: pointer;
    color: #000;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s;

    .make-similar-icon {
        width: 14px;
        height: 14px;
        object-fit: contain;
        vertical-align: middle;
    }

    span {
        line-height: 1;
    }

    &:hover {
        background: #a8ff70;
    }
}

// 旋转动画
@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

@keyframes spin-center {
    from {
        transform: translate(-50%, -50%) rotate(0deg);
    }
    to {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}

// 移动端适配
@media (max-width: 768px) {
    .outside-mask-container {
        top: 50%;
        left: 50%;
        right: auto;
        bottom: auto;
        transform: translate(-50%, -50%);
        width: 90vw;
        height: 90vh;
        padding: 0;
        background: transparent;
        overflow: hidden;
    }

    .outside-mask-container::before {
        content: '';
        position: fixed;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.7);
        z-index: -1;
    }

    .mask {
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        height: 100%;
        padding: 12px;
        border-radius: 16px;
        overflow: hidden;

        .close-btn {
            right: 10px;
            top: 10px;
            width: 44px;
            height: 44px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .music-detail-layout {
        flex-direction: column;
    }

    .music-left-panel {
        flex: none;
        height: 45%;
        min-height: 280px;
    }

    .music-right-panel {
        flex: 1;
        padding: 12px 12px 12px 12px;
    }

    .music-header {
        .song-title-single {
            font-size: 18px;
        }
    }

    .music-footer {
        flex-wrap: wrap;
        height: auto;
        gap: 8px;

        .footer-btn {
            width: 40px;
            height: 40px;

            &.like-btn {
                padding: 0 12px;
            }
        }

        .make-similar-btn {
            flex: 1 1 100%;
            height: 40px;
        }
    }
}
</style>
